import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useId, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { CheckCircle2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { type SupportMode, type SupportHint } from '@/lib/support-context';
import { usePublicConfig, useLegalIdentityDisplay } from '@/lib/public-config';
import { TurnstileWidget, HoneypotField } from '@/components/turnstile-widget';
import { BRAND, getSupportContactLabel } from '@maintenance-tracker/shared';
import { cn } from '@/lib/utils';

export function resolveSupportMode(context?: string, explicit?: SupportMode): SupportMode {
  if (explicit) return explicit;
  if (context === 'feature_idea' || context?.startsWith('feedback_') || context === 'help_page_idea') {
    return 'idea';
  }
  return 'help';
}

function resolveContext(context: string | undefined, mode: SupportMode, hint: SupportHint): string {
  if (context) return context;
  if (hint === 'import_help') return 'import_help';
  return mode === 'idea' ? 'feature_idea' : 'general';
}

/**
 * The Help form, shared by the floating widget modal and the inline /contact page.
 * Unmount to reset (the modal unmounts it on close).
 */
export function SupportForm({
  context,
  mode: modeProp = 'help',
  initialMessage = '',
  hint = null,
  onModeChange,
  onClose,
  closeLabel = 'Close',
}: {
  context?: string;
  mode?: SupportMode;
  initialMessage?: string;
  hint?: SupportHint;
  onModeChange?: (mode: SupportMode) => void;
  onClose?: () => void;
  closeLabel?: string;
}) {
  const qc = useQueryClient();
  const messageId = useId();
  const { user } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mode, setModeState] = useState<SupportMode>(modeProp);
  const [message, setMessage] = useState(initialMessage);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const [sent, setSent] = useState(false);

  const loggedIn = Boolean(user?.user);
  const { data: publicConfig } = usePublicConfig();
  const display = useLegalIdentityDisplay();
  const supportLabel = getSupportContactLabel(display);
  const turnstileSiteKey = loggedIn ? null : (publicConfig?.turnstileSiteKey ?? null);
  const isIdea = mode === 'idea';

  const setMode = (m: SupportMode) => {
    setModeState(m);
    onModeChange?.(m);
  };

  const sendMutation = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; id: string }>('/api/v1/support', {
        method: 'POST',
        body: JSON.stringify({
          message: message.trim(),
          ...(loggedIn
            ? {}
            : {
                name: name.trim() || undefined,
                email: email.trim(),
                website: website || undefined,
                turnstile_token: turnstileToken || undefined,
              }),
          page_path: pathname,
          context: resolveContext(context, mode, hint),
        }),
      }),
    onSuccess: () => {
      setSent(true);
      setMessage('');
      if (mode === 'idea' || context?.startsWith('feedback_')) {
        void qc.invalidateQueries({ queryKey: ['feedback-prompt'] });
      }
    },
  });

  if (sent) {
    return (
      <div className="space-y-4" data-testid="support-sent">
        <div className="flex items-start gap-3 rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">{isIdea ? 'Thanks — we read every idea' : 'Message sent'}</p>
            <p className="mt-1 text-[13px]">
              {isIdea
                ? 'We typically reply within one business day with next steps.'
                : 'We typically reply within one business day.'}
            </p>
          </div>
        </div>
        {onClose ? (
          <Button onClick={onClose} className="w-full">
            {closeLabel}
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => setSent(false)} className="w-full">
            Send another message
          </Button>
        )}
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        sendMutation.mutate();
      }}
    >
    {loggedIn && (
      <div className="flex rounded-lg border border-border bg-bg-subtle/40 p-1">
        <button
          type="button"
          onClick={() => setMode('help')}
          className={cn(
            'flex-1 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
            !isIdea ? 'bg-bg-surface text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary',
          )}
        >
          Get help
        </button>
        <button
          type="button"
          onClick={() => setMode('idea')}
          className={cn(
            'flex-1 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
            isIdea ? 'bg-bg-surface text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary',
          )}
        >
          Suggest something
        </button>
      </div>
    )}

    {!loggedIn && (
      <>
        <HoneypotField
          name="website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
        <Input
          label="Your email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          label="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </>
    )}

    <div>
      <label htmlFor={messageId} className="mb-1.5 block text-[13px] font-medium text-text-secondary">
        {isIdea ? 'What would help your shop?' : 'Message'}
      </label>
      <textarea
        id={messageId}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={5}
        required
        placeholder={
          isIdea
            ? 'Describe a feature, report, or workflow that would save you time…'
            : context === 'import_help' || hint === 'import_help'
              ? 'Paste a link to your spreadsheet or describe what you need imported…'
              : 'How can we help?'
        }
        className="w-full rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm transition-colors placeholder:text-text-muted focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25"
      />
    </div>

    {!loggedIn && turnstileSiteKey && (
      <TurnstileWidget siteKey={turnstileSiteKey} onToken={handleTurnstileToken} />
    )}

    {sendMutation.isError && (
      <p className="text-[13px] text-danger">{(sendMutation.error as Error).message}</p>
    )}

    <Button
      type="submit"
      loading={sendMutation.isPending}
      disabled={!message.trim() || Boolean(!loggedIn && turnstileSiteKey && !turnstileToken)}
      className="w-full"
    >
      {isIdea ? 'Send idea' : 'Send message'}
    </Button>

    <div className="rounded-lg border border-border bg-bg-subtle/50 px-3 py-2.5 text-[13px] text-text-secondary">
      <div className="flex items-start gap-2">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
        <p>
          Prefer email? Contact{' '}
          <a
            href={`mailto:${BRAND.supportEmail}?subject=${encodeURIComponent(
              isIdea ? `${BRAND.name} feature idea` : `${BRAND.name} support`,
            )}`}
            className="font-medium text-brand hover:underline"
          >
            {supportLabel}
          </a>{' '}
          for direct assistance.
        </p>
      </div>
    </div>
    </form>
  );
}
